import React from "react";
import AnswerText from "./answers/AnswerText";
import AnswerSelect from "./answers/AnswerSelect";
import AnswerMultiSelect from "./answers/AnswerMultiSelect";
import AnswerNumber from "./answers/AnswerNumber";
import AnswerDate from "./answers/AnswerDate";
import AnswerTable from "./answers/AnswerTable";
import AnswerFile from "./answers/AnswerFile";
import AnswerInstructions from "./answers/AnswerInstructions";
import QuestionCitations from "./QuestionCitations";
import QuestionInstructionBlock from "./QuestionInstructionBlock";

const isInstructionsSchema = (schema) => {
  const s = (schema || "").toLowerCase();
  return s === "instructions" || s === "instruction" || s === "info";
};

// Strip the leading "XX 1.1: " prefix from question_text when we already
// render the code as a separate chip on the left. Keeps the question text
// clean and avoids "XX 1.1: XX 1.1: ..." duplication.
const stripCodePrefix = (text, code) => {
  if (!text) return "";
  if (!code) return text;
  const escaped = code.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  return text.replace(new RegExp(`^\\s*${escaped}\\s*:\\s*`), "");
};

const renderAnswer = (q) => {
  const schema = (q.schema || "").toLowerCase();
  switch (schema) {
    case "select":
      return <AnswerSelect value={q.primary_answer} />;
    case "multi_select":
    case "multiselect":
      return <AnswerMultiSelect values={q.multi_answers ?? (q.primary_answer ? [q.primary_answer] : [])} />;
    case "number":
      return <AnswerNumber value={q.primary_answer} />;
    case "date":
      return <AnswerDate value={q.primary_answer} />;
    case "table":
      return <AnswerTable table={q.table} />;
    case "file":
      return <AnswerFile attachments={q.attachments} />;
    case "text":
    case "textarea":
    default:
      return <AnswerText value={q.primary_answer} />;
  }
};

const VqqQuestionRenderer = ({ question }) => {
  if (!question) return null;
  const code = question.question_code;
  const cleanText = stripCodePrefix(question.question_text || "", code);
  const schemaLower = (question.schema || "").toLowerCase();
  const isSelectSchema = schemaLower === "select" || (question.answer_type ?? "").toLowerCase().includes("select");

  if (isInstructionsSchema(question.schema)) {
    return (
      <article className="rounded-lg border border-emerald-100 bg-emerald-50/30 p-4">
        <div className="flex gap-4">
          <div className="shrink-0">
            {code ? (
              <span className="inline-flex items-center justify-center min-w-[3rem] h-7 px-2 rounded text-xs font-semibold bg-indigo-50 text-indigo-700 border border-indigo-200">
                {code}
              </span>
            ) : null}
          </div>
          <div className="flex-1 min-w-0 space-y-2">
            <AnswerInstructions text={question.question_text || ""} />
            <QuestionCitations citations={question.citations} />
          </div>
        </div>
      </article>
    );
  }

  return (
    <article className="rounded-lg border border-emerald-100 bg-emerald-50/30 p-4">
      <div className="flex gap-4">
        <div className="shrink-0">
          {code ? (
            <span className="inline-flex items-center justify-center min-w-[3rem] h-7 px-2 rounded text-xs font-semibold bg-indigo-50 text-indigo-700 border border-indigo-200">
              {code}
            </span>
          ) : null}
        </div>

        <div className="flex-1 min-w-0 space-y-3">
          <p className="text-sm text-gray-900 whitespace-pre-wrap leading-relaxed">{cleanText}</p>

          <QuestionInstructionBlock instructions={question.instructions} />

          <div>
            {!isSelectSchema ? (
              <div className="text-[11px] uppercase tracking-wide text-gray-500 mb-1.5">Response</div>
            ) : null}
            {renderAnswer(question)}
          </div>

          {question.comments ? (
            <div>
              <div className="text-[11px] uppercase tracking-wide text-gray-500 mb-1.5">Comments</div>
              <p className="text-sm text-gray-800 bg-white border border-emerald-100 rounded-md p-3 whitespace-pre-wrap">
                {question.comments}
              </p>
            </div>
          ) : null}

          {(question.attachments?.length ?? 0) > 0 && schemaLower !== "file" ? (
            <div>
              <div className="text-[11px] uppercase tracking-wide text-gray-500 mb-1.5">Supporting documents</div>
              <AnswerFile attachments={question.attachments} />
            </div>
          ) : null}

          <QuestionCitations citations={question.citations} />
        </div>
      </div>
    </article>
  );
};

export default VqqQuestionRenderer;
